import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect } from "react";
import { LandingChat } from "./LandingChat";
import { LandingFeatures } from "./LandingFeatures";
import { LandingFinalCTA } from "./LandingFinalCTA";
import { LandingFooter } from "./LandingFooter";
import { LandingHero } from "./LandingHero";
import { LandingNav } from "./LandingNav";
import { LandingProblem } from "./LandingProblem";
import { LandingTeam } from "./LandingTeam";
import { LandingTestimonials } from "./LandingTestimonials";

gsap.registerPlugin(ScrollTrigger);

export function LandingPage() {
  useEffect(() => {
    document.getElementById("boot-loader")?.remove();
  }, []);

  useScrollReveals();

  return (
    <>
      <title>HATO — Gestión ganadera moderna</title>
      <meta
        name="description"
        content="Corré tu finca como un sistema. Ganado, producción y sanidad en una sola plataforma."
      />

      <div className="landing-texture pointer-events-none fixed inset-0 opacity-50" aria-hidden />

      <main className="relative min-h-screen overflow-x-hidden bg-brand-dark text-white selection:bg-primary-500/20 selection:text-primary-500">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[90vh]"
          style={{
            background:
              "radial-gradient(circle at 50% 10%, rgba(0, 229, 153, 0.08) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen max-w-[1200px] flex-col border-white/[0.06] border-x bg-brand-dark/40 shadow-[0_0_80px_rgba(0,0,0,0.5)] backdrop-blur-3xl">
          <LandingNav />
          <LandingHero />
          <LandingProblem />
          <LandingChat />
          <LandingFeatures />
          <LandingTeam />
          <LandingTestimonials />
          <LandingFinalCTA />
          <LandingFooter />
        </div>
      </main>
    </>
  );
}

// ──────────────────────────────────────────────
// Scroll-driven reveals (ScrollTrigger)
//
// Matchea el comportamiento del source HATO:
// - `.js-mask-reveal` headings: texto se parte palabra a palabra
//   en <span class="mask-wrapper"><span class="scroll-word mask-inner">…</span></span>
//   y GSAP anima la inner translateY 110% → 0 con stagger cuando
//   la sección cruza el 85% del viewport.
// - `.scroll-fade` elements: opacity 0 + y:30 → opacity 1 + y:0
//   al cruzar el 85% del viewport.
// ──────────────────────────────────────────────
function useScrollReveals() {
  useEffect(() => {
    const ctx = gsap.context(() => {
      for (const el of document.querySelectorAll<HTMLElement>(".js-mask-reveal")) {
        if (el.dataset.revealed === "true") continue;
        el.dataset.revealed = "true";

        const words = el.innerText.split(" ");
        el.innerHTML = "";
        words.forEach((word, i) => {
          const wrapper = document.createElement("span");
          wrapper.className = "mask-wrapper";
          const inner = document.createElement("span");
          inner.className = "scroll-word mask-inner";
          inner.textContent = word;
          wrapper.appendChild(inner);
          el.appendChild(wrapper);
          if (i < words.length - 1) el.appendChild(document.createTextNode(" "));
        });

        gsap.to(el.querySelectorAll(".scroll-word"), {
          scrollTrigger: { trigger: el, start: "top 85%" },
          y: "0%",
          duration: 0.8,
          stagger: 0.04,
          ease: "power3.out",
        });
      }

      for (const el of gsap.utils.toArray<HTMLElement>(".scroll-fade")) {
        gsap.to(el, {
          scrollTrigger: { trigger: el, start: "top 85%" },
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
        });
      }
    });

    return () => ctx.revert();
  }, []);
}
